import React, { memo, useEffect, useState } from "react";
import styles from "./PaymentOptions.module.scss";
import { CheckmarkIcon, PaystackIcon, RefreshIcon, WalletIcon } from "../../../assets/icons";

import VFDLogoWithoutText from "../../../assets/icons/vfd-logo-without-text.jpg";
import CommonDialog from "../dialog/CommonDialog";
import CardSelectorDialog from "./component/CardSelectorDialog";
import { isCustomer } from "../../../guards/common/authentification-functions";
import { useLazyGetCustomerPaymentOptionsQuery } from "../../../service/customer-service";
import { toast } from "react-toastify";
import { useLazyGetWalletEnquiryQuery } from "../../../service/virtual-wallet-service";
import { BounceLoader } from "react-spinners";
import { CustomerPaystackCard } from "../../../model/customer/customer";

export type PaymentOption = "wallet" | "card";

type Props = {
	selectedOption?: PaymentOption;
	selectedCard?: CustomerPaystackCard;
	onSelectedCard: (card?: CustomerPaystackCard) => void;
	onSelectPaymentOption: (paymentOption: PaymentOption) => void;
};

const PaymentOptions: React.FC<Props> = (props: Props) => {
	const { selectedOption, onSelectPaymentOption, selectedCard, onSelectedCard } = props;

	const [getPaymentOptions, { data: paymentOptions }] = useLazyGetCustomerPaymentOptionsQuery();

	const [getWalletEnquiry, { isLoading: isLoadingWalletInfo, data: walletEnquiry }] = useLazyGetWalletEnquiryQuery();

	const [showCardSelector, setShowCardSelector] = useState(false);

	const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentOption | undefined>(selectedOption);

	const [currentSelectedCard, setCurrentSelectedCard] = useState(selectedCard);

	const onCloseDialog = () => {
		setShowCardSelector(false);
	};

	const selectWallet = () => {
		setSelectedPaymentOption("wallet");
	};

	const selectCard = () => {
		setSelectedPaymentOption("card");
		if (isCustomer()) {
			setShowCardSelector(true);
		}
	};

	const refreshWalletInfo = () => {
		getWalletEnquiry()
			.unwrap()
			.catch(error => {
				toast.error(error.data?.title || "Something went wrong");
			});
	}

	useEffect(() => {
		if (isCustomer()) {
			getPaymentOptions()
				.unwrap()
				.catch(error => {
					toast.error(error.data?.title || "Something went wrong");
				});
		}
	}, []);

	useEffect(() => {
		if (isCustomer()) {
			if (paymentOptions?.virtualWalletConnected) {
				getWalletEnquiry()
					.unwrap()
					.catch(error => {
						toast.error(error.data?.title || "Something went wrong");
					});
			}
		}
	}, [paymentOptions]);

	useEffect(() => {
		if(selectedPaymentOption){
			onSelectPaymentOption(selectedPaymentOption);
		}
	}, [selectedPaymentOption, onSelectPaymentOption]);

	useEffect(() => {
		setSelectedPaymentOption(selectedOption);
	}, [selectedOption]);

	useEffect(() => {
		onSelectedCard(currentSelectedCard);
	}, [currentSelectedCard, onSelectedCard]);

	useEffect(() => {
		setCurrentSelectedCard(selectedCard);
	}, [selectedCard]);

	return (
		<>
			<div className={styles.container}>
				{isCustomer() && paymentOptions?.virtualWalletConnected ? (
					<div className={styles.radioButtonContainer}>
						<input type="radio" name="paymentOption" id="wallet" checked={selectedPaymentOption === "wallet"} />
						<button className={styles.optionContainer} onClick={selectWallet}>
							<div className={styles.buttonContainer}>
								<label htmlFor="wallet">
									<CheckmarkIcon />
								</label>
							</div>
							{isLoadingWalletInfo ? (
								<div className={styles.loadingContainer}>
									<BounceLoader color="#DE841B" size={25} />
								</div>
							) : (
								<div className={styles.labelContainer}>
									<div className={styles.optionText}>
										Pay with Wallet
										<div className={styles.balance}>{`${walletEnquiry?.accountBalance.toLocaleString()} NGN`}</div>
									</div>
									<div className={`${styles.accountInfoContainer}`}>
										<img className={styles.vfdLogo} src={VFDLogoWithoutText} alt="VFD Logo" />
										<div className={styles.accountInfo}>
											<div className={styles.bank}> VFD Microfinance Bank</div>
											<div className={styles.accountNo}> {walletEnquiry?.accountNo}</div>
										</div>
										<button className={styles.refreshIcon} onClick={refreshWalletInfo} disabled={isLoadingWalletInfo}>
											<RefreshIcon color="#000" />
										</button>
									</div>
								</div>
							)}

							<div className={styles.iconContainer}>
								<WalletIcon />
							</div>
						</button>
					</div>
				) : null}
				<div className={styles.radioButtonContainer}>
					<input type="radio" name="paymentOption" id="card" checked={selectedPaymentOption === "card"} />
					<button className={styles.optionContainer} onClick={selectCard}>
						<div className={styles.buttonContainer}>
							<label htmlFor="card">
								<CheckmarkIcon />
							</label>
						</div>
						<div className={styles.optionText}>
							{
								selectedCard ? `**** **** **** ${selectedCard.last4}` : "Pay with Card"
							}
						</div>
						<div className={`${styles.iconContainer} ${styles.marginLeftAuto}`}>
							<PaystackIcon />
						</div>
					</button>
				</div>
			</div>

			{showCardSelector && (
				<CommonDialog onCloseDialog={onCloseDialog}>
					<CardSelectorDialog
						selectedCard={selectedCard}
						onCancel={onCloseDialog}
						onSelectCard={card => {
							setCurrentSelectedCard(card);
							onCloseDialog();
						}}
					/>
				</CommonDialog>
			)}
		</>
	);
};

export default PaymentOptions;
